import { APP_BASE_HREF } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { datadogLogs, Logger } from '@datadog/browser-logs';
import { take } from 'rxjs/operators';

/* eslint-disable no-shadow, @typescript-eslint/naming-convention */
enum Environment {
  production = 'production',
  stage = 'stage',
  test = 'test'
}

export enum LOG_LEVEL {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  CRITICAL = 4,
  SILENT = 99
  // every comparison is "IF level >= X" and nothing is greater than "OFF" so we can define it at the end of the enum
  // the public API does not allow to call the private log method with OFF so it will never react if that value is set
}
/* eslint-enable no-shadow, @typescript-eslint/naming-convention */

interface LoggingConfig {
  logLevel: LOG_LEVEL;
  reportLevel: LOG_LEVEL;
}

interface LoggerSetup {
  key: string;
  service: string;  
  passThrough: boolean;
  build: number;
}

interface LoggingEnvironments {
  [Environment.production]: LoggingConfig;
  [Environment.stage]: LoggingConfig;
  [Environment.test]: LoggingConfig;
}

/**
 * Logger configuration will be applied from TWO sources. This is the default which will be used immediadtely upon instantiation.
 * Afterwards !!! An ASYNC BACKEND REQUEST WILL BE ISSUED against the domain that hosts the current Application !!!
 * Under the path /logging we can provide a file
 *  - config.json
 * With the same kind of information as seen below. This will (if present) overwrite these defaults and
 * allow for "per app" logging changes for debugging and support purposes
 */
const DEFAULT_LOGGING_CONFIG: LoggingEnvironments = {
  [Environment.production]: {
    logLevel: LOG_LEVEL.SILENT,
    reportLevel: LOG_LEVEL.WARN
  },
  [Environment.stage]: {
    logLevel: LOG_LEVEL.WARN,
    reportLevel: LOG_LEVEL.WARN
  },
  [Environment.test]: {
    logLevel: LOG_LEVEL.DEBUG,
    reportLevel: LOG_LEVEL.WARN
  }
};

/**
 * This is a simple logging and reporting service that manages local storage to keep track of error message and
 * reduce the network load by sending those out in chunks. Log levels can be configured for both console logging
 * and reporting. See the setters for more explanation
 *
 * This service replicates the console API and is designed to be used instead of console within angular applications
 * It is not set up to capture console statements from third party libraries and ideally used in every ITONICS library.
 * It does not overwrite or mess with console in that features like console.table which are not available from this service
 * remain available to developers during ther development (to be removed after!) and the service can be used very precisely.
 *
 * It is set up to have a catch all for errors bubbling up to window as well as all unhandled promise rejections. Please
 * log all other information including proper error handling manually like you would with console.debug/.../console.errror
 * just write logger.debug/.../logger.error instead
 */
@Injectable({
  providedIn: 'root'
})
export class LogService {
  public canLog: boolean = false;

  private loggingConfig: LoggingConfig = {
    logLevel: LOG_LEVEL.SILENT, // by default only console statements are OFF
    reportLevel: LOG_LEVEL.WARN // by default only error is reported
  };
  private env: Environment;

  private dataDogLogs: Logger;
  private loggerSetup: LoggerSetup;
  // tracker initialized flag
  private initialized: boolean = false;

  // ******* SETUP */

  constructor(@Inject(APP_BASE_HREF) private readonly appId: string) {
    this.applyLoggingConfiguration();
    // Some global catch-all error handlers
    this.setupListeners();

    window['setLogLevel'] = (logLevel: LOG_LEVEL) => this.setLogLevel(logLevel);
    window['setReportLevel'] = (logLevel: LOG_LEVEL) => this.setReportLevel(logLevel);
    window['LogLevel'] = LOG_LEVEL;
  }

  public get loggerConfig() {
    return this.loggerSetup;
  }

  public set loggerConfig(config) {
    this.loggerSetup = config;
  }

  public applyRemoteLoggingConfiguration(http: HttpClient) {
    if (!this.env) {
      this.applyLoggingConfiguration();
    }
    http
      .get<LoggingEnvironments>(`${location.origin}/logging/config.json`)
      .pipe(take(1))
      .subscribe(
        (config) => {
          if (config) {
            this.loggingConfig = config[this.env];
            this.info(`Applied server side logging configuration`, {
              config,
              env: this.env
            });
          }
        },
        () => {
          this.info(`Application does not provide server side logging configuration.`, {
            origin: location.origin,
            env: this.env
          });
        }
      );
    return this;
  }

  // ******* DataDog Setup   ******* //
  /**
   * Setup the DataDog Log Client
   *
   * @param token DATADOG_CLIENT_TOKEN string
   * @param site  environemnt string
   * @param forwardErrorsToLogs  boolean
   * @param sampleRate  optional, default 100 number
   */
  public setupDataDog() {
    if (this.canLog && this.loggerConfig && !this.initialized) {
      // init DDogLogs
      datadogLogs.init({
        clientToken: this.loggerConfig.key,
        site: 'datadoghq.com',
        forwardErrorsToLogs: this.loggerConfig.passThrough,
        service: this.loggerConfig.service, // clippings, innovation-os, ... 
        env: this.env,                      // test, stage, live
        sessionSampleRate: 100,
        version: this.loggerConfig.build?.toString() // Bitbucket build number
      });

      // set up the dataDogLogs logger
      this.dataDogLogs = datadogLogs.createLogger(this.loggerConfig.service);
      // mark as initialized
      this.initialized = true;
      return this;
    }
  }

  // ******* BASIC PUBLIC LOGGING API */

  /**
   * Use this log method if YOU WANT TO PRINT LOW LEVEL DEBUG INFORMATION
   *
   * @param message string
   * @param args Replaces console.debug
   */
  public debug(message, payload?: Record<string, unknown>) {
    return this.logMessage(LOG_LEVEL.DEBUG, message, payload);
  }

  /**
   * Use this log method if YOU WANT TO POINT OUT SOME HIGH LEVEL INFORMATION (E.G. PROGRESS)
   *
   * @param args Replaces console.info
   */
  public info(message, payload?: Record<string, unknown>) {
    return this.logMessage(LOG_LEVEL.INFO, message, payload);
  }

  /**
   * Use this log method if SOMETHING UNUSUAL HAPPENED.
   *
   * @param args Replaces console.warn
   */
  public warn(message, payload?: Record<string, unknown>) {
    return this.logMessage(LOG_LEVEL.WARN, message, payload);
  }

  /**
   * Use this log method if SOMEBODY NEEDS TO TAKE A LOOK THE NEXT MORNING!
   *
   * @param args Replaces console.error
   */
  public error(message, payload?: Record<string, unknown>) {
    return this.logMessage(LOG_LEVEL.ERROR, message, payload);
  }

  /**
   * Use this log method if SOMEBODY NEEDS TO BE WOKEN UP AT 3AM ON A SUNDAY!
   *
   * @param args Additional method
   */
  public critical(message, payload?: Record<string, unknown>) {
    return this.logMessage(LOG_LEVEL.CRITICAL, message, payload);
  }

  // ******* CONFIGURATION  */

  /**
   * @param level Specify the log level to be used for console logging
   */
  public setLogLevel(level: LOG_LEVEL) {
    this.loggingConfig.logLevel = level;
    return this.loggingConfig.logLevel;
  }

  /**
   * @param level  Specify the log level to be used for reporting
   */
  public setReportLevel(level: LOG_LEVEL) {
    this.loggingConfig.reportLevel = level;
    this.info('Reporting level changed to', { reportLevel: this.loggingConfig.reportLevel });
    return this.loggingConfig.reportLevel;
  }

  private setupListeners() {
    // global error listener
    window.onerror = (message, source, lineno, colno, error) => {
      this.error(message, { source, lineno, colno, stack: error.stack });
    };

    // global promise rejection listener
    window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
      this.error('Unhandled, Promise Rejection', e.reason.message || e.reason);
    });
  }

  // ******* AUTO-CONFIGURATION */
  private applyLoggingConfiguration() {
    if (window.location.hostname.indexOf('itonics-test.io') > -1) {
      this.env = Environment.test;
    } else if (window.location.hostname.indexOf('itonics-stage.io') > -1) {
      this.env = Environment.stage;
    } else {
      this.env = Environment.production;
    }
    this.loggingConfig = DEFAULT_LOGGING_CONFIG[this.env];
  }

  /**
   * generate a unique error ID we can use to find error messages in DDog
   *
   * @return string
   */
  private generateErrorId(): string {
    const BASE62 = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

    const generateUri = (length: number = 15): string => {
      let sb = '';
      for (let i = 0; i < length; i++) {
        sb += BASE62.charAt(Math.random() * BASE62.length);
      }
      return sb;
    };

    return `${generateUri(4)}-${generateUri(4)}`;
  }

  // ******* LOGGING  */
  /** This could be a contract with two implementations (report implements logger and log implements logger)
   *  but this is hardly going to have to be extended so we combined it for now
   */
  private logMessage(level: LOG_LEVEL, message: string, payload: Record<string, unknown> = {}, error?: Error) {
    // Generate an ID
    const logId = this.generateErrorId();

    // If the error parameter is supplied, use it. Otherwise try and get the 
    // error from the payload (will *usually* be in the error property).
    // If there was no error parameter and no error property on the payload,
    // assume the intent was to not include a stack trace or Error object.
    if (!error) {
      if (payload && payload['error']) {
        error = payload['error'] as Error;
      }
    }
    
    // Append some values to the payload
    payload = { 
      ...payload,
      logId,
      env: this.env, 
      app: this.appId,
      version: this?.loggerSetup?.build?.toString()
    };
    
    // Check logging level
    if (level >= this.loggingConfig.logLevel) {
      /* eslint-disable */
      const konsole = {
        // eslint-disable-next-line no-console
        [LOG_LEVEL.DEBUG]: (m, p, e) => console.debug(m, p, e),
        [LOG_LEVEL.INFO]: (m, p, e) => console.info(m, p, e),
        [LOG_LEVEL.WARN]: (m, p, e) => console.warn(m, p, e),
        [LOG_LEVEL.ERROR]: (m, p, e) => console.error(m, p, e),
        [LOG_LEVEL.CRITICAL]: (m, p, e) => console.error(m, p, e),
        [LOG_LEVEL.SILENT]: () => {}
      };
      /* eslint-enable */
      konsole[level](message, payload, error);
    }

    // If the reporting level is reached and we have a Datadog instance then send logs
    if (level >= this.loggingConfig.reportLevel && this.dataDogLogs) {
      const dataDog = {
        // eslint-disable-next-line no-console
        [LOG_LEVEL.DEBUG]: (m, p, e) => this.dataDogLogs.debug(m, p, e),
        [LOG_LEVEL.INFO]: (m, p, e) => this.dataDogLogs.info(m, p, e),
        [LOG_LEVEL.WARN]: (m, p, e) => this.dataDogLogs.warn(m, p, e),
        [LOG_LEVEL.ERROR]: (m, p, e) => this.dataDogLogs.error(m, p, e),
        [LOG_LEVEL.CRITICAL]: (m, p, e) => this.dataDogLogs.error(m, p, e),
        [LOG_LEVEL.SILENT]: () => {}
      };
      /* eslint-enable */
      dataDog[level](message, payload, error);
    }

    // return the ID Number
    return logId;
  }
}
